/* import __COLOCATED_TEMPLATE__ from './ask-a-teammate.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { get } from 'embercom/lib/ajax';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import type { FinSetupBehaviorSaveParams } from 'embercom/lib/operator/fin/types';
import type IntlService from 'ember-intl/services/intl';
import type Profile from 'embercom/models/ai-agent/profile';

const FIN_EMAIL_HITL_FF = 'fin-email-hitl';
const DEFAULT_THRESHOLD = 30;

type Option = 'on' | 'off';

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  partialSave: (
    params: FinSetupBehaviorSaveParams,
    options?: {
      messages?: { success?: string; failure?: string };
      rethrow?: boolean;
    },
  ) => Promise<void> | void;
}

interface Signature {
  Args: Args;
}

export default class AskATeammate extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked selectedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  @tracked selectedThreshold: number =
    this.behavior.hitlSettings?.confidence_threshold ?? DEFAULT_THRESHOLD;
  @tracked isSaving = false;
  @tracked avgDailyEmailConversations: number | null = null;

  private savedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  private savedThreshold: number =
    this.behavior.hitlSettings?.confidence_threshold ?? DEFAULT_THRESHOLD;

  get behavior(): Profile {
    return this.args.ruleset.rulesetLinks.firstObject.object;
  }

  get canUseFinEmailHitl(): boolean {
    return Boolean(this.appService?.app?.canUseFeature?.(FIN_EMAIL_HITL_FF));
  }

  get options() {
    return [
      {
        value: 'off' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.description',
      },
      {
        value: 'on' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.description',
      },
    ];
  }

  get isEnabled(): boolean {
    return this.selectedOption === 'on';
  }

  get statusLabel(): string {
    if (!this.isEnabled) {
      return this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.disabled-status');
    }
    return this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.enabled-status', {
      threshold: this.selectedThreshold,
    });
  }

  get isInteractionDisabled(): boolean {
    return this.isSaving;
  }

  get showVolumeHelper(): boolean {
    return this.estimatedConversationsPerDay > 0;
  }

  get estimatedConversationsPerDay(): number {
    let avg = this.avgDailyEmailConversations ?? 0;
    return Math.round(avg * (this.selectedThreshold / 100));
  }

  get thresholdSliderStyle(): string {
    let pct = this.selectedThreshold;
    return sanitizeHtml(
      `background: linear-gradient(to right, var(--neutral-fill) 0%, var(--neutral-fill) ${pct}%, var(--neutral-container-emphasis) ${pct}%, var(--neutral-container-emphasis) 100%)`,
    );
  }

  get thresholdTooltipStyle(): string {
    return sanitizeHtml(
      `left: ${this.selectedThreshold}%; top: 50%; transform: translate(-50%, -50%);`,
    );
  }

  @action
  setSelectedOption(option: Option) {
    if (this.isInteractionDisabled || option === this.selectedOption) {
      return;
    }
    this.selectedOption = option;
  }

  @action
  setSelectedThreshold(threshold: number) {
    if (this.isInteractionDisabled) {
      return;
    }
    this.selectedThreshold = Math.min(100, Math.max(0, threshold));
  }

  @action
  async save(closeAccordion: () => void) {
    if (this.isInteractionDisabled) {
      return;
    }

    this.isSaving = true;

    let hitlSettings: { enabled: boolean; confidence_threshold?: number } =
      this.selectedOption === 'on'
        ? { enabled: true, confidence_threshold: this.selectedThreshold }
        : { enabled: false };

    try {
      await this.args.partialSave(
        { hitl_settings: hitlSettings },
        {
          messages: {
            success: this.intl.t(
              'operator.fin.setup.setup-and-go-live.ask-a-teammate.save-success',
            ),
            failure: this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.save-error'),
          },
          rethrow: true,
        },
      );
      this.savedOption = this.selectedOption;
      this.savedThreshold = this.selectedThreshold;
      closeAccordion();
    } catch {
      this.selectedOption = this.savedOption;
      this.selectedThreshold = this.savedThreshold;
    } finally {
      this.isSaving = false;
    }
  }

  @action
  revertPendingChanges() {
    if (this.isSaving) {
      return;
    }
    this.selectedOption = this.savedOption;
    this.selectedThreshold = this.savedThreshold;
  }

  @action
  close(closeAccordion: () => void) {
    this.revertPendingChanges();
    closeAccordion();
  }

  @action
  async loadAvgVolume() {
    let appId = this.appService?.app?.id;
    if (!appId) {
      return;
    }

    try {
      let response: { conversations_per_day: number } = await get(
        `/ember/ai_agent/workspace_volume/email_conversations_per_day?app_id=${appId}`,
      );
      let count = response?.conversations_per_day;
      this.avgDailyEmailConversations = Number.isFinite(count) ? count : null;
    } catch (e: unknown) {
      // Volume helper is non-essential; failure hides the sentence without disrupting setup.
      if (e instanceof Error) {
        console.warn('Volume estimate fetch failed:', e.message);
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::AskATeammate': typeof AskATeammate;
    'operator/fin/setup/sections/ask-a-teammate': typeof AskATeammate;
  }
}
